import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Clock3, Info, ShieldCheck, Truck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { getMyServiceProduct } from '@/lib/api';
import {
  apiErrorMessage,
  buildBreakdown,
  defaultSelection,
  humanize,
  orderingEnabled,
  unwrapEntity,
  variantId,
} from '@/lib/documentServices';
import { OrderDetails, OrderOptions } from '@/components/document-services/OrderOptions';
import {
  ServiceDisabledNotice, ServiceError, ServiceLoading, ServicePageHeader,
} from '@/components/document-services/DocumentServicesCommon';

export default function ServiceProductDetail() {
  const { productId } = useParams();
  const [product, setProduct] = useState(null);
  const [selection, setSelection] = useState({});
  const [quantity, setQuantity] = useState(1);
  const [enabled, setEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await getMyServiceProduct(productId);
      const next = unwrapEntity(data, ['product']);
      setProduct(next);
      // Opens on the cheapest combination, so the first number a buyer sees is
      // the lowest this order can cost rather than an arbitrary one.
      setSelection(defaultSelection(next));
      setEnabled(orderingEnabled(data, orderingEnabled(next, true)));
    } catch (err) {
      setError(apiErrorMessage(err, 'This service could not be loaded.'));
    } finally {
      setLoading(false);
    }
  }, [productId]);
  useEffect(() => { load(); }, [load]);

  const breakdown = useMemo(
    () => (product ? buildBreakdown(product, selection, quantity) : null),
    [product, selection, quantity],
  );
  const selectedVariant = breakdown?.variant || null;

  const chooseOption = (sectionId, value) => {
    setSelection((current) => ({ ...current, [sectionId]: value }));
  };

  if (loading) return <ServiceLoading />;
  if (error || !product) return <ServiceError message={error || 'Service not found.'} onRetry={load} />;

  const checkoutHref = selectedVariant
    ? `/student/services/${encodeURIComponent(productId)}/checkout`
      + `?variant=${encodeURIComponent(variantId(selectedVariant))}&quantity=${quantity}`
    : '';

  return (
    <div className="space-y-6 animate-fade-in">
      <ServicePageHeader
        backTo="/student/services"
        backLabel="Browse services"
        eyebrow="Document service"
        title={product.name}
        description={product.description}
      />
      {!enabled && <ServiceDisabledNotice />}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
        <div className="space-y-5">
          <OrderOptions
            product={product}
            selection={selection}
            quantity={quantity}
            onSelect={chooseOption}
            onQuantityChange={setQuantity}
          />

          <div className="grid gap-4 sm:grid-cols-2">
            <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
              <CardContent className="p-5">
                <Clock3 className="size-5 text-[#D4AF37]" />
                <h3 className="mt-3 font-semibold text-[#062B1A]">Processing</h3>
                <p className="mt-1 text-sm leading-relaxed text-[#062B1A]/55">
                  {product.processing_information
                    || 'Processing information will be confirmed with your selected option.'}
                </p>
              </CardContent>
            </Card>
            <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
              <CardContent className="p-5">
                <Truck className="size-5 text-[#D4AF37]" />
                <h3 className="mt-3 font-semibold text-[#062B1A]">Fulfillment</h3>
                <p className="mt-1 text-sm leading-relaxed text-[#062B1A]/55">
                  {product.fulfillment_information
                    || humanize(product.fulfillment_type, 'Pickup or delivery details are collected at checkout.')}
                </p>
              </CardContent>
            </Card>
          </div>

          {(product.refund_policy || product.policy) && (
            <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
              <CardContent className="p-5">
                <div className="flex items-start gap-3">
                  <Info className="mt-0.5 size-5 shrink-0 text-[#D4AF37]" />
                  <div>
                    <h3 className="font-semibold text-[#062B1A]">Refund policy</h3>
                    <p className="mt-1 text-sm leading-relaxed text-[#062B1A]/60">
                      {product.refund_policy || product.policy}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        <aside className="lg:sticky lg:top-6 lg:self-start">
          <OrderDetails
            product={product}
            breakdown={breakdown}
            footer={(
              <>
                <Button
                  asChild={Boolean(selectedVariant && enabled)}
                  disabled={!selectedVariant || !enabled}
                  className="w-full bg-[#062B1A] text-white hover:bg-[#041B10]"
                >
                  {selectedVariant && enabled
                    ? <Link to={checkoutHref}>Continue to checkout <ArrowRight className="ml-2 size-4" /></Link>
                    : <span>Checkout unavailable</span>}
                </Button>
                <p className="flex items-start gap-2 text-xs leading-relaxed text-[#062B1A]/45">
                  <ShieldCheck className="mt-0.5 size-3.5 shrink-0" />
                  Card information is entered only in Moneris Checkout.
                </p>
                <p className="flex items-start gap-2 text-xs leading-relaxed text-[#062B1A]/45">
                  <CheckCircle2 className="mt-0.5 size-3.5 shrink-0" />
                  The price is loaded and verified by MCC when the order is created.
                </p>
              </>
            )}
          />
        </aside>
      </div>
    </div>
  );
}
